import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from "react-router-dom";
import { verifyToken } from '../utils/auth';
import { getAdminMe } from "../services/api";
import Cookies from "js-cookie";
import { useOrder } from '../Salespos/EmpOrderContext';
import { useKitchenAuth } from "../context/KitchenAuthContext";
import { getStorefront, storefrontUser } from "../utils/storefront";

interface ProtectedRouteProps {
  children: React.ReactNode;
}
let hasVerifiedadminToken = false;

window.hasVerifiedToken = window.hasVerifiedToken || false;
window.hasVerifiedKitchenToken = window.hasVerifiedKitchenToken || false;

type AdminAccessState =
  | "loading"
  | "ok"
  | "unauthenticated"
  | "locked"
  | "pending_approval"
  | "needs_onboarding";

const ONBOARDING_PATH = "/admin/addhotelinformation";
const PENDING_APPROVAL_PATH = "/admin/pending-approval";

const BounceLoader: React.FC<{ color?: string }> = ({ color = "#ffcb3e" }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
    <style>{`
      @keyframes bounceDot {
        0%, 80%, 100% {
          transform: scale(0);
          opacity: 0.5;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }
      .bounce-dot {
        animation: bounceDot 1.4s infinite ease-in-out both;
      }
      .bounce-dot:nth-child(1) { animation-delay: -0.32s; }
      .bounce-dot:nth-child(2) { animation-delay: -0.16s; }
    `}</style>
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 rounded-full bounce-dot" style={{ backgroundColor: color }}></div>
      <div className="w-5 h-5 rounded-full bounce-dot" style={{ backgroundColor: color }}></div>
      <div className="w-5 h-5 rounded-full bounce-dot" style={{ backgroundColor: color }}></div>
    </div>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [access, setAccess] = useState<AdminAccessState>("loading");
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    getAdminMe()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (data?.role !== "master_admin" && data?.hasRestaurant === false) {
          setAccess("needs_onboarding");
        } else {
          setAccess("ok");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 402) {
          setAccess(
            error.response.data?.reason === "pending_approval"
              ? "pending_approval"
              : "locked",
          );
        } else {
          setAccess("unauthenticated");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (access === "loading") {
    return <BounceLoader />;
  }

  if (access === "unauthenticated") {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // billing/success is also exempt: right after Stripe redirects here, the
  // webhook that unlocks the account may not have landed yet, so "locked"
  // would otherwise bounce the user straight back to choose-plan.
  if (
    access === "locked" &&
    location.pathname !== "/admin/choose-plan" &&
    location.pathname !== "/admin/billing/success"
  ) {
    return <Navigate to="/admin/choose-plan" state={{ from: location }} replace />;
  }

  if (access === "pending_approval" && location.pathname !== PENDING_APPROVAL_PATH) {
    return <Navigate to={PENDING_APPROVAL_PATH} state={{ from: location }} replace />;
  }

  if (access === "needs_onboarding" && location.pathname !== ONBOARDING_PATH) {
    // ?setup=1 opens the "add your restaurant" form straight away.
    return <Navigate to={`${ONBOARDING_PATH}?setup=1`} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};


// ✅ Persist across SPA navigation and remounts

const EmpProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const { setUser, user } = useOrder();
  const location = useLocation();
  const token = Cookies.get("emptoken");

  useEffect(() => {
    // console.log(`This is my token `, token)
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        return;
      }

      if (!window.hasVerifiedToken) {
        const userData = await verifyToken(setIsTokenValid);
        if (userData) {
          setUser(userData);
          window.hasVerifiedToken = true; // ✅ persist for this SPA session
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
        return;
      }

      // Already verified previously
      if (user) {
        setIsTokenValid(true);
      } else {
        const userData = await verifyToken(setIsTokenValid);
        if (userData) {
          setUser(userData);
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
      }
    };

    checkToken();
  }, []); // Run only once per mount

  if (isTokenValid === null) return(
  //  <div>Loading...</div>;
  
    <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
      <style>{`
        @keyframes bounceDot {
          0%, 80%, 100% { 
            transform: scale(0);
            opacity: 0.5;
          }
          40% { 
            transform: scale(1);
            opacity: 1;
          }
        }
        .bounce-dot {
          animation: bounceDot 1.4s infinite ease-in-out both;
        }
        .bounce-dot:nth-child(1) { animation-delay: -0.32s; }
        .bounce-dot:nth-child(2) { animation-delay: -0.16s; }
      `}</style>
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-[#007bff] rounded-full bounce-dot"></div>
        <div className="w-5 h-5 bg-[#007bff] rounded-full bounce-dot"></div>
        <div className="w-5 h-5 bg-[#007bff] rounded-full bounce-dot"></div>
      </div>
    </div>
  )

  if (!isTokenValid) return <Navigate to="/login" state={{ from: location }} replace />;

  return <>{children}</>;
};




const PosProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
   const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const location = useLocation();
  const token = Cookies.get("emptoken");
  const { user, setUser, setToken } = useKitchenAuth();

  useEffect(() => {
    const checkToken = async () => {
      // Public storefront (eduw.in/<slug>): customers order without logging in.
      const storefront = getStorefront();
      if (storefront) {
        if (user?.restaurantId !== storefront.restaurantId) setUser(storefrontUser(storefront));
        setIsTokenValid(true);
        return;
      }

      if (!token) {
        setIsTokenValid(false);
        window.hasVerifiedToken = false;
        setUser(null);
        setToken(null);
        return;
      }

      // ✅ Check the flag BEFORE verification
      if (window.hasVerifiedToken) {
        if (user?.restaurantId) {
          // Already verified and user context is available
          setIsTokenValid(true);
          return;
        }

        // Verified flag is set but context is missing (e.g. refresh/login redirect path)
        const verifiedUser = await verifyToken(setIsTokenValid);
        if (!verifiedUser) {
          setIsTokenValid(false);
          setUser(null);
          return;
        }

        setUser(verifiedUser);
        setIsTokenValid(true);
        return;
      }

      // Only verify if not already verified
      const verifiedUser = await verifyToken(setIsTokenValid);
      if (!verifiedUser) {
        setIsTokenValid(false);
        setUser(null);
        setToken(null);
        return;
      }

      setUser(verifiedUser);
      setToken(token);
      window.hasVerifiedToken = true; // Mark as verified
    };

    checkToken();
  }, [token, user?.restaurantId, setToken, setUser]);

  if (isTokenValid === null) {
    return(

    <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
      <style>{`
        @keyframes bounceDot {
          0%, 80%, 100% { 
            transform: scale(0);
            opacity: 0.5;
          }
          40% { 
            transform: scale(1);
            opacity: 1;
          }
        }
        .bounce-dot {
          animation: bounceDot 1.4s infinite ease-in-out both;
        }
        .bounce-dot:nth-child(1) { animation-delay: -0.32s; }
        .bounce-dot:nth-child(2) { animation-delay: -0.16s; }
      `}</style>
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-main rounded-full bounce-dot"></div>
        <div className="w-5 h-5 bg-main rounded-full bounce-dot"></div>
        <div className="w-5 h-5 bg-main rounded-full bounce-dot"></div>
      </div>
    </div>
    )
    //  <div>Loading...</div>;
  }

  if (!isTokenValid) {
    return <Navigate to="/pos/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};


const KitchenProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const location = useLocation();
  const token = Cookies.get("emptoken");
  const { setUser, setToken } = useKitchenAuth();

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        window.hasVerifiedToken = false;
        window.hasVerifiedKitchenToken = false;
        setUser(null);
        setToken(null);
        return;
      }

      if (window.hasVerifiedKitchenToken) {
        setIsTokenValid(true);
        return;
      }

      // Always verify for kitchen to avoid stale or cross-role sessions
      const user = await verifyToken(setIsTokenValid);
      if (!user) {
        setIsTokenValid(false);
        window.hasVerifiedKitchenToken = false;
        setUser(null);
        setToken(null);
        return;
      }
      
      setUser(user);
      setToken(token);
      setIsTokenValid(true);
      window.hasVerifiedKitchenToken = true;
    };

    checkToken();
  }, []);

  if (isTokenValid === null) {
    return (
    // <div>Loading...</div>;

    <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
      <style>{`
        @keyframes bounceDot {
          0%, 80%, 100% { 
            transform: scale(0);
            opacity: 0.5;
          }
          40% { 
            transform: scale(1);
            opacity: 1;
          }
        }
        .bounce-dot {
          animation: bounceDot 1.4s infinite ease-in-out both;
        }
        .bounce-dot:nth-child(1) { animation-delay: -0.32s; }
        .bounce-dot:nth-child(2) { animation-delay: -0.16s; }
      `}</style>
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-main rounded-full bounce-dot"></div>
        <div className="w-5 h-5 bg-main rounded-full bounce-dot"></div>
        <div className="w-5 h-5 bg-main rounded-full bounce-dot"></div>
      </div>
    </div>
    );
  }

  if (!isTokenValid) {
    return <Navigate to="/kitchen/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};



export { ProtectedRoute, EmpProtectedRoute, KitchenProtectedRoute,PosProtectedRoute }; // Export both routes
